import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import logo from './assets/logo-transparent.png';
import logoDark from './assets/logo-dark.png';
import heroVisual from './assets/hero-visual.jpg';
import heroWaterfront from './assets/hero-waterfront.jpg';
import heroTownhomes from './assets/hero-townhomes.jpg';
import heroSuburban from './assets/hero-suburban.jpg';
import heroBrickRow from './assets/hero-brickrow.jpg';
import heroLakeside from './assets/hero-lakeside.jpg';
import heroForest from './assets/hero-forest.jpg';
import heroCabin from './assets/hero-cabin.jpg';

// Capability icons are inline SVGs (no image imports needed)
import storyLandlord from './assets/story-landlord.jpg';
import storyTenant from './assets/story-tenant.jpg';
import ctaPanelImage from './assets/cta-panel.jpg';
import { Routes, Route, Link, NavLink, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { initHaptics } from './utils/haptics';
import './theme.css';
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import PropertyList from './components/PropertyList';
import PropertyDetailComponent from './components/PropertyDetail'; // Renamed to avoid conflict
import LandlordDashboard from './components/LandlordDashboard';
import BentoDashboard from './components/BentoDashboard';
import PropertyForm from './components/PropertyForm';
import TenantDashboard from './components/TenantDashboard'; // Import TenantDashboard
import TenantPayments from './components/TenantPayments';
import TenantInviteAccept from './components/TenantInviteAccept';
import TenantMessages from './components/TenantMessages';
import TenantMaintenance from './components/TenantMaintenance';
import LandlordMessages from './components/LandlordMessages';
import NotificationCenter from './components/NotificationCenter';
import ErrorBoundary from './components/ErrorBoundary';
import TenantApplications from './components/TenantApplications';
import TenantLease from './components/TenantLease';
import ApplicationForm from './components/ApplicationForm'; // Import ApplicationForm
import AdminDashboard from './components/AdminDashboard'; // Import AdminDashboard
import VerificationInfo from './components/VerificationInfo';
import Pricing from './components/Pricing';
import Partners from './components/Partners';
import PropertyManagers from './components/PropertyManagers';
import OrgSettings from './components/OrgSettings';
import OrgTeam from './components/OrgTeam';
import OwnerDashboard from './components/OwnerDashboard';
import FaqAccordion from './components/FaqAccordion';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Refunds from './components/Refunds';
import TosGate from './components/TosGate';
import DocumentUpload from './components/DocumentUpload'; // Import DocumentUpload
import ApplicantList from './components/ApplicantList'; // Import ApplicantList

import LandlordViewings from './components/LandlordViewings';
import RentLedger from './components/RentLedger';

import ApplicationReview from './components/ApplicationReview'; // Import ApplicationReview
import LeaseSigning from './components/LeaseSigning';
import TenantApplicationView from './components/TenantApplicationView'; // Import Tenant view
import MyTenants from './components/MyTenants';
import MyTenantDetail from './components/MyTenantDetail';
import MaintenanceRequests from './components/MaintenanceRequests';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import ContactPage from './components/ContactPage';
import SupportPage from './components/SupportPage';
import LeadCaptureForm from './components/LeadCaptureForm';
import ScreeningLanding from './components/ScreeningLanding';
import Platform from './components/Platform';
import ScreenTenant from './components/ScreenTenant';
import ScreeningSubmit from './components/ScreeningSubmit';
import ScreeningList from './components/ScreeningList';
import ScreeningReport from './components/ScreeningReport';
import SampleReport from './components/SampleReport';
import HomeV2 from './components/HomeV2';
import BlogIndex from './blog/BlogIndex';
import BlogPost from './blog/BlogPost';
import LeaseSigningLanding from './components/LeaseSigningLanding';
import RentalApplicationsLanding from './components/RentalApplicationsLanding';
import PlatformDemo from './components/PlatformDemo';
import { trackEvent } from './utils/analytics';

const GA_MEASUREMENT_ID = 'G-R18D5S2Z6P';
const META_PIXEL_ID = '939076632139637';
const ENABLE_AUTO_LOGOUT = true;

// GA4 with consent mode: loads immediately in cookieless/restricted mode,
// upgrades to full tracking when user accepts cookies.
// This ensures page views are always tracked (even without consent)
// while respecting privacy preferences for ads/remarketing.
const loadGaScript = (consentGranted = false) => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(){window.dataLayer.push(arguments);};

  // Set default consent state BEFORE loading the script
  if (!window.__gaConsentSet) {
    window.gtag('consent', 'default', {
      analytics_storage: 'granted',   // PIPEDA (Canada) allows analytics without consent
      ad_storage: 'denied',           // Ad tracking requires consent (GDPR + best practice)
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
    });
    window.__gaConsentSet = true;
  }

  // Load the gtag.js script once
  if (!window.__gaLoaded) {
    const scriptId = 'ga4-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);
    }
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: true });
    window.__gaLoaded = true;
  }

  // Upgrade consent when user accepts
  if (consentGranted) {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    });
  }
};

const loadMetaPixel = () => {
  if (typeof window === 'undefined') return;
  if (window.__fbPixelInit) return;
  if (window.fbq) {
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
    window.__fbPixelInit = true;
    return;
  }
  (function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ?
        n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
  window.__fbPixelInit = true;
};


// Create an Auth Context
export const AuthContext = createContext(null);

function Header() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem('rentual_theme') || 'dark';
  });

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('rentual_theme', theme);
  }, [theme]);

  useEffect(() => { initHaptics(); }, []);

  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');
  const [unreadCount, setUnreadCount] = useState(0);
  const [maintenanceUnread, setMaintenanceUnread] = useState(0);
  const [leaseUnread, setLeaseUnread] = useState(0);
  const [applicationsUnread, setApplicationsUnread] = useState(0);
  const [tenantsUnread, setTenantsUnread] = useState(0);
  const [tenantsNavOpen, setTenantsNavOpen] = useState(false);
  const [messagesNavOpen, setMessagesNavOpen] = useState(false);
  const [orgNavOpen, setOrgNavOpen] = useState(false);
  const [propertiesUnread, setPropertiesUnread] = useState(0);

  const [ledgerUnread, setLedgerUnread] = useState(0);
  const [tenantPaymentsUnread, setTenantPaymentsUnread] = useState(0);
  const [notificationsUnread, setNotificationsUnread] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMenuOpen(false);
  };

  

  useEffect(() => {
    const fetchUnread = async () => {
      if (!user || !user.token) {
        setUnreadCount(0);
        setMaintenanceUnread(0);
        setApplicationsUnread(0);
        setTenantsUnread(0);
        setPropertiesUnread(0);
        setLedgerUnread(0);
        setTenantPaymentsUnread(0);
        setNotificationsUnread(0);
        return;
      }
      if (!(user.userType === 'tenant' || user.userType === 'landlord')) {
        setUnreadCount(0);
        setMaintenanceUnread(0);
        setApplicationsUnread(0);
        setTenantsUnread(0);
        setPropertiesUnread(0);
        setLedgerUnread(0);
        setTenantPaymentsUnread(0);
        return;
      }
      try {
        const [messagesRes, maintenanceRes, leaseRes, applicationsRes, tenantsRes, propertiesRes, ledgerRes, tenantPaymentsRes, notificationsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/messages/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/lease/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/properties/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ledger/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payments/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` },
          })
        ]);

        if (messagesRes.ok) {
          const data = await messagesRes.json();
          setUnreadCount(data.count || 0);
        }
        if (maintenanceRes.ok) {
          const data = await maintenanceRes.json();
          setMaintenanceUnread(data.count || 0);
        }
        if (tenantsRes.ok) {
          const data = await tenantsRes.json();
          setTenantsUnread(data.count || 0);
        }
        if (propertiesRes.ok) {
          const data = await propertiesRes.json();
          setPropertiesUnread(data.count || 0);
        }
        if (leaseRes.ok) {
          const data = await leaseRes.json();
          setLeaseUnread(data.count || 0);
        }
        if (applicationsRes.ok) {
          const data = await applicationsRes.json();
          setApplicationsUnread(data.count || 0);
        }
        if (ledgerRes.ok) {
          const data = await ledgerRes.json();
          setLedgerUnread(data.count || 0);
        }
        if (tenantPaymentsRes.ok) {
          const data = await tenantPaymentsRes.json();
          setTenantPaymentsUnread(data.count || 0);
        }
        if (notificationsRes.ok) {
          const data = await notificationsRes.json();
          setNotificationsUnread(data.count || 0);
        }
      } catch (err) {
        // silent fail
      }
    };

    const handleViewed = () => setUnreadCount(0);
    const handleMaintenanceViewed = () => setMaintenanceUnread(0);
    const handleTenantsViewed = () => setTenantsUnread(0);
    const handlePropertiesViewed = () => setPropertiesUnread(0);
    const handleLedgerViewed = () => setLedgerUnread(0);
    const handleTenantPaymentsViewed = () => setTenantPaymentsUnread(0);
    const handleApplicationsViewed = () => setApplicationsUnread(0);
    const handleNotificationsViewed = () => setNotificationsUnread(0);

    fetchUnread();
    window.addEventListener('messages:viewed', handleViewed);
    window.addEventListener('maintenance:viewed', handleMaintenanceViewed);
    window.addEventListener('tenants:viewed', handleTenantsViewed);
    window.addEventListener('properties:viewed', handlePropertiesViewed);
    window.addEventListener('ledger:viewed', handleLedgerViewed);
    window.addEventListener('tenant-payments:viewed', handleTenantPaymentsViewed);
    window.addEventListener('applications:viewed', handleApplicationsViewed);
    window.addEventListener('notifications:viewed', handleNotificationsViewed);
    const interval = setInterval(fetchUnread, 30000);
    return () => {
      window.removeEventListener('messages:viewed', handleViewed);
      window.removeEventListener('maintenance:viewed', handleMaintenanceViewed);
      window.removeEventListener('tenants:viewed', handleTenantsViewed);
      window.removeEventListener('properties:viewed', handlePropertiesViewed);
      window.removeEventListener('ledger:viewed', handleLedgerViewed);
      window.removeEventListener('tenant-payments:viewed', handleTenantPaymentsViewed);
      window.removeEventListener('applications:viewed', handleApplicationsViewed);
      window.removeEventListener('notifications:viewed', handleNotificationsViewed);
      clearInterval(interval);
    };
  }, [user]);

  const toggleMenu = () => {
    setMenuOpen((prev) => {
      const next = !prev;
      if (next) {
        setTenantsNavOpen(false);
        setMessagesNavOpen(false);
      }
      return next;
    });
  };

  const handleNavLinkClick = () => {
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navLinkClass = ({ isActive }) => `nav-link${isActive ? ' active' : ''}`;
  const navMessageClass = ({ isActive }) => `nav-message-link${isActive ? ' active' : ''}`;
  const tenantsGroupUnread = applicationsUnread + tenantsUnread;
  const messagesGroupUnread = unreadCount + maintenanceUnread;

  useEffect(() => {
    document.body.classList.add('page-reveal');
    return () => document.body.classList.remove('page-reveal');
  }, []);

  return (
    <>
      <header className={`App-header ${user ? 'app-sidebar' : ''}`}>
      <nav className={`navbar ${!user ? 'navbar-public' : ''}`}>
        <div className="nav-header">
          <Link to="/" className="brand-logo"><img src={theme === 'dark' ? logoDark : logo} alt="rentual.co Logo" /></Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button onClick={toggleTheme} className="theme-toggle theme-toggle-mobile" title={theme === 'light' ? 'Dark mode' : 'Light mode'}>
              {theme === 'light' ? (
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M15.5 9.5a6.5 6.5 0 01-7-7 6.5 6.5 0 107 7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.5"/><path d="M9 1.5v2M9 14.5v2M1.5 9h2M14.5 9h2M3.7 3.7l1.4 1.4M12.9 12.9l1.4 1.4M14.3 3.7l-1.4 1.4M5.1 12.9l-1.4 1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              )}
            </button>
            <button className={`nav-toggle ${menuOpen ? 'open' : ''}`} onClick={toggleMenu} aria-label="Toggle navigation" style={{ position: 'relative' }}>
              <span></span><span></span><span></span>
              {!menuOpen && (() => {
                const total = (propertiesUnread || 0) + (applicationsUnread || 0) + (tenantsUnread || 0) + (unreadCount || 0) + (maintenanceUnread || 0) + (ledgerUnread || 0) + (tenantPaymentsUnread || 0) + (leaseUnread || 0) + (notificationsUnread || 0);
                return total > 0 ? (
                  <span className="nav-badge" style={{ position: 'absolute', top: '-4px', right: '-4px', fontSize: '0.6rem', minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                    {total > 99 ? '99+' : total}
                  </span>
                ) : null;
              })()}
            </button>
          </div>
        </div>
        <ul className={`nav-links ${menuOpen ? 'visible' : ''}`} style={menuOpen ? { opacity: 1, pointerEvents: 'auto', maxHeight: '70vh' } : undefined}>
          <li><NavLink to={user ? '/screen' : '/'} end onClick={handleNavLinkClick} className={navLinkClass}>Screen</NavLink></li>
          <li><NavLink to="/platform" onClick={handleNavLinkClick} className={navLinkClass}>Platform</NavLink></li>
          <li><NavLink to="/blog" onClick={handleNavLinkClick} className={navLinkClass}>Insights</NavLink></li>
          {user && user.userType === 'landlord' && (
            <li><NavLink to="/landlord/dashboard/v2" onClick={handleNavLinkClick} className={navLinkClass}>Dashboard</NavLink></li>
          )}
          {user && user.userType === 'tenant' && (
            <li><NavLink to="/tenant/dashboard" onClick={handleNavLinkClick} className={navLinkClass}>Dashboard</NavLink></li>
          )}
          {!user && (
            <>
              <li><NavLink to="/pricing" onClick={handleNavLinkClick} className={navLinkClass}>Pricing</NavLink></li>
              <li><NavLink to="/partners" onClick={handleNavLinkClick} className={navLinkClass}>Partners</NavLink></li>
            </>
          )}
          {user && user.userType === 'landlord' && (
            <li>
              <NavLink to="/landlord/dashboard" end onClick={handleNavLinkClick} className={navMessageClass}>
                <span>My Properties</span>
                {propertiesUnread > 0 && <span className="nav-badge">{propertiesUnread}</span>}
              </NavLink>
            </li>
          )}
          {user && user.userType === 'landlord' && (
            <li className={`nav-group ${tenantsNavOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="nav-group-toggle nav-message-link"
                onClick={() => setTenantsNavOpen((prev) => !prev)}
                aria-expanded={tenantsNavOpen}
              >
                <span>Tenants</span>
                {tenantsGroupUnread > 0 && <span className="nav-badge">{tenantsGroupUnread}</span>}
                <span className="nav-caret">▾</span>
              </button>
              <ul className={`nav-sub-links ${tenantsNavOpen ? 'open' : ''}`}>
                <li>
                  <NavLink to="/landlord/viewings" onClick={handleNavLinkClick} className={navMessageClass}>
                    <span>Viewings</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/landlord/applicants" onClick={handleNavLinkClick} className={navMessageClass}>
                    <span>Applicants</span>
                    {applicationsUnread > 0 && <span className="nav-badge">{applicationsUnread}</span>}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/landlord/my-tenants" onClick={handleNavLinkClick} className={navMessageClass}>
                    <span>Tenants</span>
                    {tenantsUnread > 0 && <span className="nav-badge">{tenantsUnread}</span>}
                  </NavLink>
                </li>
              </ul>
            </li>
          )}
          {user && user.userType === 'landlord' && (
            <li className={`nav-group ${messagesNavOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="nav-group-toggle nav-message-link"
                onClick={() => setMessagesNavOpen((prev) => !prev)}
                aria-expanded={messagesNavOpen}
              >
                <span>Messages</span>
                {messagesGroupUnread > 0 && <span className="nav-badge">{messagesGroupUnread}</span>}
                <span className="nav-caret">▾</span>
              </button>
              <ul className={`nav-sub-links ${messagesNavOpen ? 'open' : ''}`}>
                <li>
                  <NavLink to="/landlord/messages" onClick={handleNavLinkClick} className={navMessageClass}>
                    <span>Messages</span>
                    {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/landlord/maintenance" onClick={handleNavLinkClick} className={navMessageClass}>
                    <span>Maintenance</span>
                    {maintenanceUnread > 0 && <span className="nav-badge">{maintenanceUnread}</span>}
                  </NavLink>
                </li>
              </ul>
            </li>
          )}
          {user && user.userType === 'landlord' && (
            <li>
              <NavLink to="/landlord/ledger" onClick={handleNavLinkClick} className={navMessageClass}>
                <span>Payments</span>
                {ledgerUnread > 0 && <span className="nav-badge">{ledgerUnread}</span>}
              </NavLink>
            </li>
          )}
          {user && user.userType === 'landlord' && (
            <li>
              <NavLink to="/landlord/notifications" onClick={handleNavLinkClick} className={navMessageClass}>
                <span>Notifications</span>
                {notificationsUnread > 0 && <span className="nav-badge">{notificationsUnread}</span>}
              </NavLink>
            </li>
          )}
          {user && user.userType === 'landlord' && (() => { try { return !!localStorage.getItem('rentual_org'); } catch { return false; } })() && (
            <li className={`nav-group ${orgNavOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="nav-group-toggle nav-message-link"
                onClick={() => setOrgNavOpen((prev) => !prev)}
                aria-expanded={orgNavOpen}
              >
                <span>Organization</span>
                <span className="nav-caret">▾</span>
              </button>
              <ul className={`nav-sub-links ${orgNavOpen ? 'open' : ''}`}>
                <li><NavLink to="/org/settings" onClick={handleNavLinkClick} className={navLinkClass}><span>Settings</span></NavLink></li>
                <li><NavLink to="/org/team" onClick={handleNavLinkClick} className={navLinkClass}><span>Team</span></NavLink></li>
              </ul>
            </li>
          )}

          {user && user.userType === 'tenant' && (
            <li>
              <NavLink to="/tenant/payments" onClick={handleNavLinkClick} className={navMessageClass}>
                <span>Payments</span>
                {tenantPaymentsUnread > 0 && <span className="nav-badge">{tenantPaymentsUnread}</span>}
              </NavLink>
            </li>
          )}
          {user && user.userType === 'tenant' && (
            <li>
              <NavLink to="/tenant/applications" onClick={handleNavLinkClick} className={navMessageClass}>
                <span>Applications</span>
                {applicationsUnread > 0 && <span className="nav-badge">{applicationsUnread}</span>}
              </NavLink>
            </li>
          )}
          {user && user.userType === 'tenant' && (
            <li>
              <NavLink to="/tenant/lease" onClick={handleNavLinkClick} className={navMessageClass}>
                <span>Lease</span>
                {leaseUnread > 0 && <span className="nav-badge">{leaseUnread}</span>}
              </NavLink>
            </li>
          )}
          {user && user.userType === 'tenant' && (
            <li>
              <NavLink to="/tenant/messages" onClick={handleNavLinkClick} className={navMessageClass}>
                <span>Messages</span>
                {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
              </NavLink>
            </li>
          )}
          {user && user.userType === 'tenant' && (
            <li>
              <NavLink to="/tenant/maintenance" onClick={handleNavLinkClick} className={navMessageClass}>
                <span>Maintenance</span>
                {maintenanceUnread > 0 && <span className="nav-badge">{maintenanceUnread}</span>}
              </NavLink>
            </li>
          )}
          {user && user.userType === 'tenant' && (
            <li>
              <NavLink to="/tenant/notifications" onClick={handleNavLinkClick} className={navMessageClass}>
                <span>Notifications</span>
                {notificationsUnread > 0 && <span className="nav-badge">{notificationsUnread}</span>}
              </NavLink>
            </li>
          )}
          {user && user.is_admin && user.userType !== 'tenant' && (
            <li><NavLink to="/admin/dashboard" onClick={handleNavLinkClick} className="nav-link nav-admin-link">Admin</NavLink></li>
          )}
          {!user ? (
            <>
              <li><NavLink to="/login" onClick={handleNavLinkClick} className={navLinkClass}>Login</NavLink></li>
              <li><NavLink to="/register" onClick={handleNavLinkClick} className={navLinkClass}>Register</NavLink></li>
            </>
          ) : (
            <li><button onClick={handleLogout} className="logout-button">Logout</button></li>
          )}
          <li>
            <button onClick={toggleTheme} className="theme-toggle" title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
              {theme === 'light' ? (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M15.5 9.5a6.5 6.5 0 01-7-7 6.5 6.5 0 107 7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.5"/><path d="M9 1.5v2M9 14.5v2M1.5 9h2M14.5 9h2M3.7 3.7l1.4 1.4M12.9 12.9l1.4 1.4M14.3 3.7l-1.4 1.4M5.1 12.9l-1.4 1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              )}
            </button>
          </li>
          {user && (
            <li style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color, #e2e8f0)', paddingTop: '0.5rem' }}>
              <Link to="/contact" onClick={handleNavLinkClick} className="nav-link" style={{ fontSize: '0.75rem', opacity: 0.55, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 5v3M8 10.5h.007M14.5 8A6.5 6.5 0 111.5 8a6.5 6.5 0 0113 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Report a bug
              </Link>
            </li>
          )}
        </ul>
      </nav>
    </header>

    </>
  );
}

function Footer({ onOpenCookieSettings = () => {} }) {
  return (
    <footer className="App-footer">
      <p>&copy; 2026 rentual.co. All rights reserved.</p>
      <div className="footer-links">
        <Link to="/pricing" className="footer-link">Pricing</Link>
        <Link to="/verification" className="footer-link">Verification</Link>
        <Link to="/terms" className="footer-link">Terms</Link>
        <Link to="/privacy" className="footer-link">Privacy</Link>
        <Link to="/refunds" className="footer-link">Refunds</Link>
        <Link to="/property-managers" className="footer-link">Property Managers</Link>
        <Link to="/contact" className="footer-link">Contact</Link>
        <Link to="/blog" className="footer-link">Insights</Link>
        <Link to="/support" className="footer-link">Support</Link>
        <button type="button" className="footer-link footer-link-button" onClick={onOpenCookieSettings}>
          Cookie settings
        </button>
      </div>
    </footer>
  );
}

function Home() {
  const capabilities = [
    {
      title: 'Application pipeline',
      body: 'Track every applicant stage with AI-assisted summaries and clear next steps.',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M9 7h6M9 11h6M9 15h4"/><path d="M15 14l1.5 1.5L20 12" stroke="#28b6c8"/></svg>
    },
    {
      title: 'Secure document vault',
      body: 'Store sensitive files securely and keep every lease and attachment organized.',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20V8l8-5 8 5v12"/><rect x="9" y="13" width="6" height="7" rx="1"/><circle cx="12" cy="17" r="1" fill="currentColor" stroke="none"/><path d="M10 13v-2a2 2 0 014 0v2"/></svg>
    },
    {
      title: 'Rent payments + autopay',
      body: 'Collect rent by card or bank, track status, and automate follow-ups.',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/><path d="M6 15h4"/><path d="M18 8a3 3 0 11-3 3" stroke="#28b6c8"/><path d="M16.5 9.5L18 8l1.5 1.5" stroke="#28b6c8"/></svg>
    },
    {
      title: 'Verified operators',
      body: 'Build tenant trust with verified property managers and credible listings.',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" fill="rgba(40,182,200,0.1)" stroke="#28b6c8"/><path d="M9 12l2 2 4-4"/></svg>
    },
    {
      title: 'Messaging in context',
      body: 'Keep tenant conversations tied to applications and properties.',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><path d="M8 9h8M8 13h5"/></svg>
    }
  ];

  const stories = [
    {
      id: 'story-landlords',
      title: 'Built for property managers',
      copy: 'Manage multi-property portfolios with team access, owner reporting, and centralized operations. From single buildings to city-wide portfolios.',
      bullets: ['Screen applicants with AI-powered dossiers and credit checks', 'Collect rent by card with fee controls, autopay, and ledger tracking', 'Assign team members, track performance, and report to owners'],
      align: 'left',
      image: storyLandlord
    },
    {
      id: 'story-tenants',
      title: 'A better tenant experience',
      copy: 'Tenants get a guided experience from first viewing to move-in, with clear screening steps, fast communication, and online rent payments.',
      bullets: ['Submit applications and documents in one place', 'Pay rent online and access receipts any time', 'Know exactly where you stand through screening and leasing'],
      align: 'right',
      image: storyTenant
    }
  ];

  const heroSlides = [
    heroVisual,
    heroForest,
    heroTownhomes,
    heroSuburban,
    heroBrickRow,
    heroCabin,
    heroWaterfront,
    heroLakeside
  ];
  const heroTestimonials = [
    {
      quote: '"We cut vacancy time by two weeks just by keeping everything in one place."',
      name: 'Avery C.',
      role: 'Portfolio manager, 120 units'
    },
    {
      quote: '"Our leasing team gets answers out faster because everything is in one dashboard."',
      name: 'Lena P.',
      role: 'Operations director'
    },
    {
      quote: '"Applicants feel guided instead of overwhelmed. It\'s a night-and-day difference."',
      name: 'Steve V.',
      role: 'Property management company'
    },
    {
      quote: '"Renewals and move-outs are finally organized. No more hunting through emails."',
      name: 'Jason R.',
      role: 'Multi-building portfolio'
    },
    {
      quote: '"It looks polished for tenants, and it\'s simpler for us to manage."',
      name: 'Marcus T.',
      role: 'Condo management firm'
    },
    {
      quote: '"Tenants appreciate the transparency. Fewer back-and-forth messages."',
      name: 'Daniel K.',
      role: 'Heritage portfolio lead'
    },
    {
      quote: '"We went from spreadsheets to a real workflow. Our team actually uses it."',
      name: 'Priya S.',
      role: 'Regional property manager'
    },
    {
      quote: '"Docs, tours, and renewals live in one place. It saves us hours every week."',
      name: 'Amelia J.',
      role: 'Leasing coordinator'
    }
  ];


  const [heroIndex, setHeroIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  const activeTestimonial = heroTestimonials[heroIndex % heroTestimonials.length];

  return (
    <main className="App-main home-main">
      <section id="hero" className="hero-section">
        <div className="hero-content">
          <p className="eyebrow-tag">Lease + payments platform · Powered by AI</p>
          <h1>
            <span className="hero-line-big">Rental operations, simplified.</span>
            <span className="hero-highlight">From portfolio to payment in one platform.</span>
          </h1>
          <div className="hero-ctas">
            <Link className="primary-button" to="/register">Start your free trial</Link>
            <a href="/#steps" className="ghost-button" onClick={(e) => { e.preventDefault(); document.getElementById('steps')?.scrollIntoView({ behavior: 'smooth' }); }}>See how it works</a>
          </div>
          <div className="hero-metrics">
            <div>
              <span>AI Assisted</span>
              <p>Summaries + risk flags in seconds</p>
            </div>
            <div>
              <span>Payments</span>
              <p>Autopay + clear status tracking</p>
            </div>
            <div>
              <span>Leases</span>
              <p>Auto-fill &amp; e-sign in minutes</p>
            </div>
            <div>
              <span>Viewings</span>
              <p>Scheduling + listing controls</p>
            </div>
            <div>
              <span>Automated</span>
              <p>Reminders, updates, next steps</p>
            </div>
            <div>
              <span>Verified</span>
              <p>Screening + application checks</p>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-carousel">
            <div
              className="hero-carousel-track"
              style={{ transform: `translateX(-${heroIndex * 100}%)` }}
            >
              {heroSlides.map((slide, idx) => (
                <div className="hero-slide" key={`hero-slide-${idx}`}>
                  <img
                    src={slide}
                    alt="rentual.co luxury rental showcase"
                    className="hero-image"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="hero-testimonial" key={`testimonial-${heroIndex}`}>
            <p>{activeTestimonial.quote}</p>
            <div className="hero-testimonial-meta">
              <span>{activeTestimonial.name}</span>
              <span>{activeTestimonial.role}</span>
            </div>
          </div>
        </div>
      </section>

      <PlatformDemo />

      <section id="capabilities" className="capabilities-section">
        <p className="eyebrow-tag">What makes rentual.co different</p>
        <h2>Everything your leasing team needs. Nothing it doesn't.</h2>
        <div className="capabilities-grid">
          {capabilities.map((cap, idx) => (
            <article key={cap.title} className="capability-card">
              <div className="capability-icon">{cap.icon}</div>
              <h3>{cap.title}</h3>
              <p>{cap.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* How it works section hidden for now */}

      {stories.map((story, idx) => (
        <section id={story.id} key={story.title} className={`story-band ${story.align === 'right' ? 'reverse' : ''}`}>
          <div className="story-visual">
            {story.image ? (
              <img src={story.image} alt={story.title} className="story-image" />
            ) : (
              <>
                <div className="story-gradient"></div>
                <div className="story-collage">
                  <div className="story-orb"></div>
                  <div className="story-lines"></div>
                  <div className="story-card">
                    <p>Workflow focus</p>
                    <strong>{story.title}</strong>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="story-copy">
            <p className="eyebrow-tag">{idx === 0 ? 'For operators' : 'For tenants'}</p>
            <h3>{story.title}</h3>
            <p>{story.copy}</p>
            <ul>
              {story.bullets.map(point => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        </section>
      ))}

      <section className="trust-section">
        <p className="eyebrow-tag">Trust built in</p>
        <h2>Compliance and security your portfolio depends on.</h2>
        <div className="trust-grid">
          <article className="trust-card">
            <h3>Canadian Compliance</h3>
            <p>Workflows designed around Canadian residential tenancy standards, PIPEDA privacy requirements, and provincial regulations.</p>
          </article>
          <article className="trust-card">
            <h3>Verified Operators</h3>
            <p>We verify operator identities and property details to protect tenants and keep the platform credible.</p>
          </article>
          <article className="trust-card">
            <h3>Secure by Design</h3>
            <p>Files, messages, and applications are encrypted in transit and protected with role-based access.</p>
          </article>
          <article className="trust-card">
            <h3>Secure Payments</h3>
            <p>Card and bank payments run through Stripe with clear fees, receipts, and an audit trail.</p>
          </article>
        </div>
      </section>

      <section className="faq-section">
        <p className="eyebrow-tag">FAQ</p>
        <h2>Questions before you start?</h2>
        <FaqAccordion items={[
          { question: 'Is Rentual built for property managers?', answer: 'Yes. Team access, owner reporting, portfolio analytics, and workflows designed for commercial operators across Canada.' },
          { question: 'How fast can I get a listing live?', answer: 'Most operators can publish a property in under 10 minutes.' },
          { question: 'Do you store applicant documents securely?', answer: 'Yes. Files are encrypted in transit and protected with role-based access.' },
          { question: 'Can I collect rent through Rentual?', answer: 'Yes. Enable card or bank payments (pre-authorized debit) and autopay after you connect Stripe.' },
          { question: 'Can I cancel anytime?', answer: 'Yes. Plans are month-to-month, cancel any time.' },
          { question: 'How do rent payment fees work?', answer: 'Landlords set a platform fee and can choose to pass card fees to tenants.' },
          { question: 'Is autopay available for tenants?', answer: 'Yes. Tenants can pay by card or bank transfer (pre-authorized debit) and enable autopay for on-time payments.' },
          { question: 'Are payment receipts and records included?', answer: 'Yes. Every payment includes a receipt and audit trail in the rent ledger.' }
        ]} />
      </section>

      <section
        id="cta"
        className="cta-panel"
        style={{ backgroundImage: `linear-gradient(135deg, rgba(10, 10, 10, 0.65), rgba(10, 10, 10, 0.3)), url(${ctaPanelImage})` }}
      >
        <h3>Ready to run your portfolio on one platform?</h3>
        <p>Start free. Scale to any size. No credit card required.</p>
        <div className="hero-ctas">
          <Link className="primary-button" to="/register">Start your free trial</Link>
          <Link className="ghost-button" to="/contact">See how it works</Link>
        </div>
      </section>
    </main>
  );
}

function NotFound() {
  return (
    <main className="App-main">
      <h2>404 - Page Not Found</h2>
      <p>Sorry, the page you are looking for does not exist.</p>
      <Link to="/">Go to Home</Link>
    </main>
  );
}

// Custom PropertyDetailComponent to include dynamic actions
// (This is a simplified version; in a real app, PropertyDetailComponent would take `user` prop and render buttons)
// For the MVP, we're just making sure the buttons are linked correctly
function PropertyDetailWithActions() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();

  return (
    <PropertyDetailComponent propertyId={id} user={user} />
  );
}

function scrollToHash(targetHash) {
  const hash = targetHash || window.location.hash;
  if (!hash) return;
  const el = document.querySelector(hash);
  if (!el) return;

  const start = window.scrollY;
  const headerOffset = 96;
  const target = el.getBoundingClientRect().top + window.scrollY - headerOffset;
  const distance = target - start;
  const duration = 500;
  let startTime = null;

  const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

  const step = (timestamp) => {
    if (!startTime) startTime = timestamp;
    const progress = Math.min((timestamp - startTime) / duration, 1);
    const eased = easeInOut(progress);
    window.scrollTo(0, start + distance * eased);
    if (progress < 1) {
      window.requestAnimationFrame(step);
    }
  };

  window.requestAnimationFrame(step);
}

const LeaseCompleteRedirect = ({ role }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const load = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/leases/${id}/esign?sync=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Unable to load signing status');
        const appId = data.request?.application_id;
        if (role === 'landlord' && appId) {
          navigate(`/landlord/application/${appId}`);
          return;
        }
        if (role === 'tenant') {
          if (appId) {
            navigate(`/application/${appId}/documents?signed=1`);
          } else {
            navigate('/tenant/lease');
          }
          return;
        }
        navigate('/landlord/applicants');
      } catch (err) {
        setError(err.message || 'Unable to complete signing');
      }
    };
    load();
  }, [id, role, navigate]);

  return (
    <main className="App-main">
      <div className="dashboard-section" style={{ maxWidth: '520px' }}>
        <h3>Finalizing signature</h3>
        <p className="status-text" style={{ marginTop: '0.35rem' }}>
          {error || 'Redirecting you back to the lease...'}
        </p>
      </div>
    </main>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [transitionKey, setTransitionKey] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [overlayDuration, setOverlayDuration] = useState(900);

  const [analyticsConsent, setAnalyticsConsent] = useState(() => {
    if (typeof window === 'undefined') return 'unknown';
    return localStorage.getItem('rentual_cookie_consent') || 'unknown';
  });

  const acceptAnalytics = useCallback(() => {
    localStorage.setItem('rentual_cookie_consent', 'accepted');
    setAnalyticsConsent('accepted');
  }, []);

  const declineAnalytics = useCallback(() => {
    localStorage.setItem('rentual_cookie_consent', 'declined');
    setAnalyticsConsent('declined');
  }, []);

  const reopenConsent = useCallback(() => {
    localStorage.removeItem('rentual_cookie_consent');
    setAnalyticsConsent('unknown');
  }, []);


  const login = useCallback((token, userType, userId, isAdmin) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userType', userType);
    localStorage.setItem('userId', userId);
    localStorage.setItem('isAdmin', isAdmin);
    setUser({ token, userType, userId, is_admin: isAdmin });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('rentual_org');
    localStorage.removeItem('tos_accepted');
    setUser(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    const userId = localStorage.getItem('userId');
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (token && userType && userId) {
      setUser({ token, userType, userId: parseInt(userId), is_admin: isAdmin }); // Ensure userId is number
    }
    setAuthReady(true);
  }, []);

  useEffect(() => {
    if (location.pathname === '/') {
      const pendingHash = sessionStorage.getItem('pendingHash');
      if (pendingHash) {
        sessionStorage.removeItem('pendingHash');
        setTimeout(() => scrollToHash(pendingHash), 100);
        return;
      }
      if (location.hash) {
        setTimeout(() => scrollToHash(location.hash), 100);
      }
    }
  }, [location]);

  useEffect(() => {
    trackEvent({
      event_name: 'page_view',
      event_category: 'navigation',
      event_action: `${location.pathname}${location.search}${location.hash}`
    });
  }, [location]);

  // Scroll to top on any route change (pathname or search params)
  const prevPath = React.useRef(location.pathname + location.search);
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    if (currentPath !== prevPath.current && !location.hash) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    prevPath.current = currentPath;
  }, [location.pathname, location.search, location.hash]);

  // Load GA immediately (cookieless consent mode) — always tracks page views
  useEffect(() => {
    loadGaScript(analyticsConsent === 'accepted');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Upgrade consent + load Meta Pixel when user accepts
  useEffect(() => {
    if (analyticsConsent === 'accepted') {
      loadGaScript(true);
      loadMetaPixel();
    }
  }, [analyticsConsent]);

  useEffect(() => {
    const handleRouteTransition = (event) => {
      const durationIn = event?.detail?.durationIn ?? 250;
      const hold = event?.detail?.hold ?? 100;
      const durationOut = event?.detail?.durationOut ?? 350;
      setOverlayDuration(durationIn);
      setTransitioning(true);
      setTimeout(() => {
        setOverlayDuration(durationOut);
        setTransitioning(false);
      }, durationIn + hold);
    };
    window.addEventListener('route-transition', handleRouteTransition);
    return () => window.removeEventListener('route-transition', handleRouteTransition);
  }, []);

  const [showReloginPrompt, setShowReloginPrompt] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState('');

  useEffect(() => {
    let handled = false;
    const handleExpired = () => {
      if (handled) return;
      if (!localStorage.getItem('token')) return;
      handled = true;
      const redirect = `${location.pathname}${location.search}${location.hash}`;
      setPendingRedirect(redirect);
      setShowReloginPrompt(true);
    };
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!showReloginPrompt) return;
    const timer = setTimeout(() => {
      setShowReloginPrompt(false);
      logout();
      const redirect = pendingRedirect || '/';
      navigate(`/login?reason=expired&redirect=${encodeURIComponent(redirect)}`);
    }, 5000);
    return () => clearTimeout(timer);
  }, [showReloginPrompt, pendingRedirect, navigate, logout]);

  useEffect(() => {
    if (!ENABLE_AUTO_LOGOUT) return;
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await originalFetch(...args);
      try {
        const token = localStorage.getItem('token');
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        if (token && res.status === 401 && !url.includes('/api/auth/login')) {
          window.dispatchEvent(new Event('auth:expired'));
        }
      } catch (err) {
        // ignore
      }
      return res;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  useEffect(() => {
    setTransitionKey(prev => prev + 1);
  }, [location.pathname]);

  const marketingRoutes = new Set(['/', '/pricing', '/terms', '/privacy', '/refunds', '/contact', '/platform']);
  const isMarketingRoute = marketingRoutes.has(location.pathname) || location.pathname.startsWith('/screening/submit');

  const RequireAuth = ({ children }) => {
    if (!authReady) return <main className="App-main">Loading...</main>;
    if (!user) {
      const redirectTarget = `${location.pathname}${location.search}`;
      return <Navigate to={`/login?redirect=${encodeURIComponent(redirectTarget)}`} replace />;
    }
    return children;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, authReady }}>
      <div className={`App ${user ? 'app-with-sidebar' : ''} ${user && !isMarketingRoute ? 'app-logged-in' : ''}`}>
        <Header />
        {showReloginPrompt && (
          <div className="modal-overlay">
            <div className="modal-card relogin-modal">
              <h3>Session expired</h3>
              <p className="status-text">Please log in again to continue.</p>
              <div className="form-actions" style={{ marginTop: '1rem' }}>
                <button
                  className="primary-button"
                  onClick={() => {
                    setShowReloginPrompt(false);
                    logout();
                    const redirect = pendingRedirect || '/';
                    navigate(`/login?reason=expired&redirect=${encodeURIComponent(redirect)}`);
                  }}
                >
                  Log in
                </button>
                <button
                  className="secondary-button"
                  onClick={() => {
                    setShowReloginPrompt(false);
                    logout();
                    navigate('/login?reason=expired');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        <TosGate />
        <div
          className={`route-overlay${transitioning ? ' visible' : ''}`}
          style={{ '--route-fade-duration': `${overlayDuration}ms` }}
          aria-hidden="true"
        ></div>
        <div key={transitionKey} className="route-fade">
          <div className={`App-main ${isMarketingRoute ? 'app-main-marketing' : ''}`}>
          <ErrorBoundary>
          <Routes>
          <Route path="/" element={<HomeV2 />} />
          <Route path="/v1" element={<Home />} />
          <Route path="/properties" element={(
            <RequireAuth>
              <PropertyList />
            </RequireAuth>
          )} />
          <Route path="/properties/:id" element={<PropertyDetailWithActions />} />

          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/lead-capture" element={<LeadCaptureForm />} />
          <Route path="/screening" element={<ScreeningLanding />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/screen" element={<ScreenTenant />} />
          <Route path="/screening/submit/:token" element={<ScreeningSubmit />} />
          <Route path="/landlord/screenings" element={<ScreeningList />} />
          <Route path="/landlord/screenings/:id" element={<ScreeningReport />} />
          <Route path="/sample-report" element={<SampleReport />} />
          <Route path="/lease-signing" element={<LeaseSigningLanding />} />
          <Route path="/rental-applications" element={<RentalApplicationsLanding />} />
          <Route path="/verification" element={<VerificationInfo />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/property-managers" element={<PropertyManagers />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refunds" element={<Refunds />} />
          {/* Landlord Specific Routes */}
          <Route path="/landlord/dashboard" element={<LandlordDashboard />} />
          <Route path="/landlord/dashboard/v2" element={<BentoDashboard />} />
          <Route path="/landlord/properties/new" element={<PropertyForm />} />
          <Route path="/landlord/properties/edit/:id" element={<PropertyForm />} />
          <Route path="/landlord/applicants" element={<ApplicantList />} />
          <Route path="/landlord/viewings" element={<LandlordViewings />} />

          <Route path="/landlord/application/:id" element={<ApplicationReview />} />
          <Route path="/landlord/applications/:id/lease" element={<LeaseSigning />} />
          <Route path="/landlord/leases/:id/complete" element={<LeaseCompleteRedirect role="landlord" />} />
          <Route path="/landlord/my-tenants" element={<MyTenants />} />
          <Route path="/landlord/my-tenants/:id" element={<MyTenantDetail />} />
          <Route path="/landlord/messages" element={<LandlordMessages />} />
          <Route path="/landlord/maintenance" element={<MaintenanceRequests />} />
          <Route path="/landlord/settings" element={<RentLedger initialTab="settings" />} />
          <Route path="/landlord/ledger" element={<RentLedger />} />
          <Route path="/landlord/analytics" element={<AnalyticsDashboard />} />
          <Route path="/landlord/notifications" element={<NotificationCenter />} />
          <Route path="/org/settings" element={<OrgSettings />} />
          <Route path="/org/team" element={<OrgTeam />} />
          <Route path="/owner/dashboard" element={<OwnerDashboard />} />
          <Route path="/tenant/application/:id" element={<TenantApplicationView />} />
          {/* Tenant Specific Routes */}
          <Route path="/tenant/dashboard" element={<TenantDashboard />} />
          <Route path="/tenant/payments" element={<TenantPayments />} />
          <Route path="/tenant/invite/accept" element={<TenantInviteAccept />} />
          <Route path="/tenant/applications" element={<TenantApplications />} />
          <Route path="/tenant/lease" element={<TenantLease />} />
          <Route path="/tenant/leases/:id/complete" element={<LeaseCompleteRedirect role="tenant" />} />
          <Route path="/tenant/messages" element={<TenantMessages />} />
          <Route path="/tenant/maintenance" element={<TenantMaintenance />} />
          <Route path="/tenant/notifications" element={<NotificationCenter />} />
          <Route path="/apply/:propertyId" element={<ApplicationForm />} />
          <Route path="/application/:id/documents" element={<DocumentUpload />} />
          <Route path="/properties/:propertyId/apply" element={<ApplicationForm />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </ErrorBoundary>
        </div>
        <Footer onOpenCookieSettings={reopenConsent} />
      </div>
    </div>
    {analyticsConsent === 'unknown' && (
      <div className="cookie-banner" role="dialog" aria-live="polite">
        <p>
          We use cookies to improve your experience and measure site performance.
          Accept for the best experience, or continue with basic analytics only.
          See our <Link to="/privacy">Privacy Policy</Link>.
        </p>
        <div className="cookie-banner-actions">
          <button className="primary-button" onClick={acceptAnalytics}>Accept all</button>
          <button className="secondary-button" onClick={declineAnalytics}>Continue</button>
        </div>
      </div>
    )}
    </AuthContext.Provider>
  );
}

export default App;



